import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, BookOpen, Volume2, Type, ChevronRight } from 'lucide-react';
const Home = () => {
  const navigate = useNavigate();
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState<'normal' | 'large'>('normal');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const timeOfDay = new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening';
  const handleSpeak = (text: string) => {
    setIsSpeaking(true);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };
  return <div className="flex flex-col min-h-[calc(100vh-130px)] pt-2" data-id="element-386">
      {/* Header with Font Size Toggle */}
      <div className="flex justify-between items-start mb-4" data-id="element-387">
        <div className="flex-1" data-id="element-388">
          <h1 className={`${fontSize === 'large' ? 'text-3xl' : 'text-2xl'} font-medium mb-2 dark:text-white`} data-id="element-389">
            Good {timeOfDay}, Maria
          </h1>
        </div>
        <button onClick={() => setFontSize(f => f === 'normal' ? 'large' : 'normal')} className="p-2 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md 
            transition-all text-gray-600 dark:text-gray-300 ml-4" aria-label="Adjust text size" data-id="element-390">
          <Type className="w-5 h-5" data-id="element-391" />
        </button>
      </div>
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col gap-4" data-id="element-392">
        {/* Mood Check-in */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-soft" data-id="element-393">
          <div className="flex justify-between items-center mb-3" data-id="element-394">
            <h2 className={`${fontSize === 'large' ? 'text-2xl' : 'text-xl'} font-medium dark:text-white`} data-id="element-395">
              How are you feeling today?
            </h2>
            <button onClick={() => handleSpeak('How are you feeling today?')} className="p-2 rounded-lg text-lavender hover:bg-lavender/10 transition-all" aria-label="Read aloud" data-id="element-396">
              <Volume2 className="w-5 h-5" data-id="element-397" />
            </button>
          </div>
          <div className="flex gap-3" data-id="element-398">
            {[{
            emoji: '😊',
            label: 'Good',
            color: 'mint-green'
          }, {
            emoji: '😐',
            label: 'Okay',
            color: 'lavender'
          }, {
            emoji: '😔',
            label: 'Low',
            color: 'soft-coral'
          }].map(mood => <button key={mood.label} onClick={() => setSelectedMood(mood.label)} className={`
                  flex-1 py-4 px-3 
                  bg-gradient-to-b from-${mood.color}/20 to-${mood.color}/5
                  hover:from-${mood.color}/30 hover:to-${mood.color}/10
                  rounded-2xl transition-all duration-200
                  ${selectedMood === mood.label ? `ring-2 ring-${mood.color} shadow-lg` : 'shadow-sm hover:shadow'}
                  border border-${mood.color}/20
                `} data-id="element-399">
                <span className="block text-4xl mb-2" data-id="element-400">{mood.emoji}</span>
                <span className={`${fontSize === 'large' ? 'text-lg' : 'text-base'} font-medium text-gray-700 dark:text-gray-200`} data-id="element-401">
                  {mood.label}
                </span>
              </button>)}
          </div>
        </div>
        {/* Main Actions */}
        <div className="grid grid-cols-2 gap-4" data-id="element-402">
          <button onClick={() => navigate('/chat')} className="p-5 bg-gradient-to-b from-lavender/20 to-lavender/5 
              hover:from-lavender/30 hover:to-lavender/10
              rounded-2xl transition-all duration-200 text-left
              shadow-sm hover:shadow-md border border-lavender/20" data-id="element-403">
            <div className="w-12 h-12 bg-lavender/20 rounded-xl flex items-center justify-center mb-3" data-id="element-404">
              <MessageCircle className="w-6 h-6 text-lavender" data-id="element-405" />
            </div>
            <h3 className={`${fontSize === 'large' ? 'text-xl' : 'text-lg'} font-medium mb-1 text-gray-800 dark:text-white`} data-id="element-406">
              Chat with COMO
            </h3>
            <p className={`${fontSize === 'large' ? 'text-base' : 'text-sm'} text-gray-600 dark:text-gray-300`} data-id="element-407">
              Let's talk about your day
            </p>
          </button>
          <button onClick={() => navigate('/memory')} className="p-5 bg-gradient-to-b from-mint-green/20 to-mint-green/5 
              hover:from-mint-green/30 hover:to-mint-green/10
              rounded-2xl transition-all duration-200 text-left
              shadow-sm hover:shadow-md border border-mint-green/20" data-id="element-408">
            <div className="w-12 h-12 bg-mint-green/20 rounded-xl flex items-center justify-center mb-3" data-id="element-409">
              <BookOpen className="w-6 h-6 text-mint-green" data-id="element-410" />
            </div>
            <h3 className={`${fontSize === 'large' ? 'text-xl' : 'text-lg'} font-medium mb-1 text-gray-800 dark:text-white`} data-id="element-411">
              Daily Journal
            </h3>
            <p className={`${fontSize === 'large' ? 'text-base' : 'text-sm'} text-gray-600 dark:text-gray-300`} data-id="element-412">
              Record your memories
            </p>
          </button>
        </div>
        {/* Quick Tip */}
        <div className="bg-gradient-to-br from-lavender/10 to-mint-green/10 
          rounded-2xl p-4 shadow-sm border border-lavender/10" data-id="element-413">
          <div className="flex items-start space-x-3" data-id="element-414">
            <div className="bg-white dark:bg-slate-800 p-2.5 rounded-xl" data-id="element-415">
              <span className="text-xl" data-id="element-416">💡</span>
            </div>
            <div className="flex-1 min-w-0" data-id="element-417">
              <h3 className={`${fontSize === 'large' ? 'text-lg' : 'text-base'} font-medium mb-1 dark:text-white`} data-id="element-418">
                Quick Tip
              </h3>
              <p className={`${fontSize === 'large' ? 'text-base' : 'text-sm'} text-gray-600 dark:text-gray-300`} data-id="element-419">
                Tap the speaker icon anywhere to hear text read aloud
              </p>
            </div>
            <button onClick={() => handleSpeak('Tap the speaker icon anywhere to hear text read aloud')} className="p-2 rounded-lg text-lavender hover:bg-lavender/10 transition-all flex-shrink-0" aria-label="Read tip aloud" data-id="element-420">
              <Volume2 className="w-5 h-5" data-id="element-421" />
            </button>
          </div>
        </div>
        {/* Recent Updates */}
        <div className="space-y-2.5" data-id="element-422">
          <h2 className="text-lg font-medium px-1 text-gray-800 dark:text-white" data-id="element-423">
            Recent Updates
          </h2>
          <button onClick={() => navigate('/family')} className="w-full flex items-center p-3.5 bg-white/80 dark:bg-slate-800/80 
              hover:bg-white dark:hover:bg-slate-800 rounded-xl transition-all duration-200
              shadow-sm hover:shadow border border-lavender/10" data-id="element-424">
            <div className="w-10 h-10 bg-lavender/20 rounded-full flex items-center justify-center" data-id="element-425">
              <MessageCircle className="w-5 h-5 text-lavender" data-id="element-426" />
            </div>
            <div className="flex-1 text-left ml-3 min-w-0" data-id="element-427">
              <p className="font-medium text-gray-800 dark:text-white truncate" data-id="element-428">
                New message from Sarah
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-429">
                10 minutes ago
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-lavender/70 ml-2" data-id="element-430" />
          </button>
          <button onClick={() => navigate('/memory')} className="w-full flex items-center p-3.5 bg-white/80 dark:bg-slate-800/80 
              hover:bg-white dark:hover:bg-slate-800 rounded-xl transition-all duration-200
              shadow-sm hover:shadow border border-mint-green/10" data-id="element-431">
            <div className="w-10 h-10 bg-mint-green/20 rounded-full flex items-center justify-center" data-id="element-432">
              <BookOpen className="w-5 h-5 text-mint-green" data-id="element-433" />
            </div>
            <div className="flex-1 text-left ml-3 min-w-0" data-id="element-434">
              <p className="font-medium text-gray-800 dark:text-white truncate" data-id="element-435">
                Memory: Garden Photos
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-436">
                2 hours ago
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-mint-green/70 ml-2" data-id="element-437" />
          </button>
        </div>
      </div>
    </div>;
};
export default Home;